import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import styles from "./Layout.module.css";
import { DefaultButton, IButtonStyles, ICommandBarStyles, Label, Stack, MessageBar, MessageBarType, Shimmer, ShimmerElementType } from "@fluentui/react";
import { useEffect, useState } from "react";
import { getCompanyIcon } from "../../api";
import { getMenuList } from "../../api/admin/api_admin";
import { MenuItem, APIError } from "../../api/admin/models_admin";

interface MenuItemButtonProps {
    text: string;
    to: string;
}

const MenuItemButton = (props: MenuItemButtonProps) => {
    const { text, to } = props;
    const location = useLocation();
    const isActive = location.pathname.startsWith(to);
    
    return (
        <Link 
            to={to} 
            style={{
                display: 'block',
                textDecoration: 'none',
                color: 'inherit',
                fontSize: "12pt"
            }}
        >
            <div 
                style={{
                    padding: '3px 3px',
                    cursor: 'pointer',
                    backgroundColor: isActive ? '#E6E6E6' : 'transparent',
                }}
                onMouseEnter={(e) => {
                    if (!isActive) {
                        e.currentTarget.style.backgroundColor = '#E6E6E6';
                    }
                }}
                onMouseLeave={(e) => {
                    if (!isActive) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                    }
                }}
            >
                {text}
            </div>
        </Link>
    );
};

const LayoutAdmin = () => {
    const adminMenuButtonStyles: ICommandBarStyles & IButtonStyles = {
      root: {
          width: '170px',
          border: `1px solid #D1D1D1`,
          padding: '5px 12px',
        },
    };
    const navigate = useNavigate()
    const [imageUrl, setImageUrl] = useState<string | undefined>();
    const [menulist, setMenulist] = useState<MenuItem[]>([]);
    const [error, setError] = useState<string | null>(null);

    // APIErrorをチェックしてダイアログで表示
    const isAPIError = (response: any): response is APIError => {
        return response && typeof response === 'object' && 'error' in response;
    };

    const showErrorDialog = (message: string) => {
        setError(message);
    };

    useEffect(() => {
      const fetchImage = async () => {
          try {
            const objectURL = await getCompanyIcon();
            setImageUrl(objectURL);
          } catch (error) {
            console.error('クライアント側アイコン取得例外エラー:', error);
          }
      };
      fetchImage();
      const fetchMenu = async () => {
          const menues = await getMenuList();
          if (isAPIError(menues)) {
              showErrorDialog(menues.error);
          } else {
              setMenulist(menues ?? []);
          }
      };
      fetchMenu()
    }, []);

    return (
        <div className={styles.layout}>
            <header className={styles.header} role={"banner"}>
                <Stack horizontal verticalAlign="center" horizontalAlign="space-between" style={{padding: "0px 20px"}}>
                    <Stack horizontal verticalAlign="center" tokens={{childrenGap: "16px"}}>
                        <Shimmer shimmerElements={[{ type: ShimmerElementType.line, width: 120, height: 36 }]} isDataLoaded={Boolean(imageUrl)}>
                            <img
                                draggable={false}
                                src={imageUrl}
                                className={styles.headerIcon}
                                aria-hidden="true"
                                onClick={async () => {
                                    const code = await fetch("/").then(r => r.status)
                                    const redirect_dst = code !== 200 ? '/login/' + localStorage.getItem('WorQuforGAI_companykey') : '/'
                                    navigate(redirect_dst)
                                }}
                            />
                        </Shimmer>
                        <div className={styles.headerTitleContainer}>
                            <h1 className={styles.headerTitle}>WorQu for GAI</h1>
                        </div>
                        <DefaultButton
                            onClick={() => window.close()}
                            styles={adminMenuButtonStyles}
                            text="閉じる"
                        />
                    </Stack>
                </Stack>
            </header>
            <Stack horizontal styles={{ root: { flex: 1, overflow: 'hidden' } }}>
                <div style={{
                    flex: "1",
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px",
                    background: "radial-gradient(108.78% 108.78% at 50.02% 19.78%, #FFFFFF 57.29%, #EEF6FE 100%)",
                    boxShadow: "0 2px 4px #00000024, 0 0 2px #0000001f",
                    borderRadius: "8px",
                    marginLeft: "20px",
                    marginRight: "20px",
                    marginBottom: "45px",
                    overflow: "auto",
                    padding: "30px 60px"
                }}>
                    <Outlet />
                </div>
                <Stack style={{
                    width: "294px", 
                    paddingLeft: "10px",
                    paddingTop: "10px",
                    marginRight: "10px"
                }}>
                    <Label style={{fontWeight: 600, fontSize: "20px", color: "#58535E", marginBottom: "10px"}}>管理者メニュー</Label>
                    <Stack style={{listStyleType: 'none', padding: 0, margin: 0}}>
                        <Shimmer 
                            shimmerElements={[
                                { type: ShimmerElementType.line, width: "100%", height: 300 }
                            ]} 
                            isDataLoaded={menulist.length !== 0 || !!error}
                        >
                            {error && (
                                <MessageBar messageBarType={MessageBarType.error}>
                                    {error}
                                </MessageBar>
                            )}
                            {menulist.map((v, idx) => (<MenuItemButton key={idx} text={v.function_name} to={`/menu/${v.route_name}`}/>))}
                        </Shimmer>
                    </Stack>
                </Stack>
            </Stack>
        </div>
    );
};

export default LayoutAdmin;